import { Suspense, lazy, useState } from 'react';

import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, FormMessage, Input, InputGroup } from 'components/FormComponents';

import { useUpdateUserPasswordMutation } from 'api/user-api';

const PasswordStrengthBar = lazy(() => import('react-password-strength-bar'));

export const ChangePassword = ({ user, setLoading = () => {}, messagePopUp = () => {} }) => {
  const { t } = useTranslation();

  const [updatePassword] = useUpdateUserPasswordMutation();

  const {
    control,
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const newPasswordValue = useWatch({ control, name: 'new_password' });
  const currentPasswordValue = useWatch({ control, name: 'current_password' });

  const [error, setError] = useState();
  const [scorePass, setScorePass] = useState(false);

  const handleUpdate = async data => {
    if (!scorePass) {
      setError('Password is not strong enough');
      return;
    }

    if (currentPasswordValue === newPasswordValue) {
      setError('New password cannot be the same as the current password.');
      return;
    }

    if (data.new_password !== data.new_password_confirm) {
      setError("Passwords don't match");
      return;
    }

    setError();
    setLoading(true);
    const { data: response, error: requestError } = await updatePassword({
      id: user.id,
      current_password: data.current_password,
      new_password: data.new_password,
    });
    if (response?.error) setError(response.error.message);
    else if (requestError?.status === 400) setError(requestError.data?.error?.log);
    else if (requestError) setError(t('account.tabs.security.password.updateFailed'));
    else {
      messagePopUp('Password updated', MESSAGE_STATE_SUCCESS);
      reset();
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(handleUpdate)}>
      <FormMessage message={error} className="u-margin-top--0" onClose={() => setError(null)} />
      <InputGroup title="Current Password">
        <Input
          name="current_password"
          type="password"
          placeholder="Enter password"
          register={register}
          validators={{ required: true }}
          error={errors.current_password?.type}
        />
      </InputGroup>
      <div className="u-divider " />
      <InputGroup title="New Password">
        <Input
          name="new_password"
          type="password"
          placeholder="Enter password"
          register={register}
          validators={{ required: true }}
          error={errors.new_password?.type}
        />
        <Suspense fallback={<div>Loading password strength...</div>}>
          <PasswordStrengthBar
            password={newPasswordValue}
            onChangeScore={score => {
              score > 2 ? setScorePass(true) : setScorePass(false);
            }}
          />
        </Suspense>
      </InputGroup>
      <InputGroup title="Confirm New Password">
        <Input
          name="new_password_confirm"
          type="password"
          placeholder="Enter password"
          register={register}
          validators={{ required: true }}
          error={errors.new_password_confirm?.type}
        />
      </InputGroup>
      <Button className="u-width-100 u-margin-top--large" submit>
        Update Password
      </Button>
    </form>
  );
};
